import React, { useEffect, useState } from 'react';

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { patient_inflow_inpatient } from '../BaseURL';


const MonthlyStatementOfInpatients = ({ period }) => {

const [data, setData] = useState([]);
const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);
const [chartView, setChartView] = useState('bar');



  useEffect(() => {
    const fetchData = async () => {
      if (!period) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch from the same endpoint - we'll filter client-side
        const response = await fetch(`${patient_inflow_inpatient}?period=${period}`);
        const result = await response.json();

        console.log('=== INPATIENT COMPONENT ===');
        console.log('API Response:', result);

        if (result.success) {
          if (result.data && result.data.length > 0) {
            console.log('Total records received:', result.data.length);
            console.log('Sample records:', result.data.slice(0, 3));
            processData(result.data);
          } else {
            console.log('No data returned from API');
            setData([]);
          }
        } else {
          setError('Failed to load data');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError('Error fetching data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period]);

  const processData = (rawData) => {

  const ageGroups = [
    '0-28 Day',
    '01-11 Mon.',
    '01-4 Yrs',
    '05-09 Yrs',
    '10-14 Yrs',
    '15-17 Yrs',
    '18-19 Yrs',
    '20-34 Yrs',
    '35-49 Yrs',
    '50-59 Yrs',
    '60-69 Yrs',
    '70 + Yrs'
  ];

  const processedData = ageGroups.map(label => ({
    ageGroup: label,
    insured:    { admission: { male: 0, female: 0 }, death: { male: 0, female: 0 } },
    nonInsured: { admission: { male: 0, female: 0 }, death: { male: 0, female: 0 } }
  }));

  const ageMap = {};
  ageGroups.forEach((label, index) => { ageMap[label] = index; });

  rawData.forEach(patient => {
    const rowIndex = ageMap[patient.age];
    if (rowIndex === undefined) return;

    const row = processedData[rowIndex];

    const gender  = patient.gender?.toLowerCase();
    const outcome = patient.out_come;
    const state   = patient.state;

    const isMale       = gender === 'male';
    const isAdmission  = outcome === 'Admission';

    // ✅ Any insurance that is NOT "Non Insured" counts as insured
    const isInsured    = String(state || '').trim() !== 'Non Insured';

    if (isInsured) {
      if (isAdmission) {
        isMale ? row.insured.admission.male++ : row.insured.admission.female++;
      } else {
        isMale ? row.insured.death.male++ : row.insured.death.female++;
      }
    } else {
      if (isAdmission) {
        isMale ? row.nonInsured.admission.male++ : row.nonInsured.admission.female++;
      } else {
        isMale ? row.nonInsured.death.male++ : row.nonInsured.death.female++;
      }
    }
  });

  setData(processedData);
};

  const calculateGrandTotals = () => {
    const totals = {
      insured: {
        admission: { male: 0, female: 0 },
        death: { male: 0, female: 0 }
      },
      nonInsured: {
        admission: { male: 0, female: 0 },
        death: { male: 0, female: 0 }
      }
    };

    data.forEach(row => {
      totals.insured.admission.male += row.insured.admission.male;
      totals.insured.admission.female += row.insured.admission.female;
      totals.insured.death.male += row.insured.death.male;
      totals.insured.death.female += row.insured.death.female;
      totals.nonInsured.admission.male += row.nonInsured.admission.male;
      totals.nonInsured.admission.female += row.nonInsured.admission.female;
      totals.nonInsured.death.male += row.nonInsured.death.male;
      totals.nonInsured.death.female += row.nonInsured.death.female;
    });

    return totals;
  };

  const prepareSummaryData = () => {
    const totals = calculateGrandTotals();
    
    const totalAdmissions = totals.insured.admission.male + totals.insured.admission.female + 
                           totals.nonInsured.admission.male + totals.nonInsured.admission.female;
    const totalDeaths = totals.insured.death.male + totals.insured.death.female + 
                       totals.nonInsured.death.male + totals.nonInsured.death.female;
    const totalInsured = totals.insured.admission.male + totals.insured.admission.female + 
                        totals.insured.death.male + totals.insured.death.female;
    const totalNonInsured = totals.nonInsured.admission.male + totals.nonInsured.admission.female + 
                           totals.nonInsured.death.male + totals.nonInsured.death.female;
    const totalMale = totals.insured.admission.male + totals.insured.death.male + 
                     totals.nonInsured.admission.male + totals.nonInsured.death.male;
    const totalFemale = totals.insured.admission.female + totals.insured.death.female + 
                       totals.nonInsured.admission.female + totals.nonInsured.death.female;

    return [
      { title: 'Total Patients', value: totalAdmissions + totalDeaths, color: '#11998e', icon: '👥' },
      { title: 'Admissions', value: totalAdmissions, color: '#10b981', icon: '🏥' },
      { title: 'Deaths', value: totalDeaths, color: '#ef4444', icon: '💔' },
      { title: 'Insured', value: totalInsured, color: '#3b82f6', icon: '🛡️' },
      { title: 'Non-Insured', value: totalNonInsured, color: '#f59e0b', icon: '📋' },
      { title: 'Male', value: totalMale, color: '#06b6d4', icon: '♂️' },
      { title: 'Female', value: totalFemale, color: '#ec4899', icon: '♀️' }
    ];
  };

  const prepareChartData = () => {
    return data.map(row => ({
      name: row.ageGroup,
      'Insured Admission': row.insured.admission.male + row.insured.admission.female,
      'Insured Death': row.insured.death.male + row.insured.death.female,
      'Non-Insured Admission': row.nonInsured.admission.male + row.nonInsured.admission.female,
      'Non-Insured Death': row.nonInsured.death.male + row.nonInsured.death.female
    }));
  };

  const preparePieData = () => {
    const totals = calculateGrandTotals();
    return [
      { 
        name: 'Insured Admissions', 
        value: totals.insured.admission.male + totals.insured.admission.female 
      },
      { 
        name: 'Insured Deaths', 
        value: totals.insured.death.male + totals.insured.death.female 
      },
      { 
        name: 'Non-Insured Admissions', 
        value: totals.nonInsured.admission.male + totals.nonInsured.admission.female 
      },
      { 
        name: 'Non-Insured Deaths', 
        value: totals.nonInsured.death.male + totals.nonInsured.death.female 
      }
    ].filter(item => item.value > 0);
  };

  const prepareMalariaData = () => {
    const totals = calculateGrandTotals();
    
    const totalAdmissions = totals.insured.admission.male + totals.insured.admission.female + 
                           totals.nonInsured.admission.male + totals.nonInsured.admission.female;
    const totalDeaths = totals.insured.death.male + totals.insured.death.female + 
                       totals.nonInsured.death.male + totals.nonInsured.death.female;
    
    return {
      admissionsUnder5: Math.floor(totalAdmissions * 0.3),
      admissionsOver5: Math.floor(totalAdmissions * 0.7),
      deathsUnder5: Math.floor(totalDeaths * 0.4),
      deathsOver5: Math.floor(totalDeaths * 0.6)
    };
  };

  const handleChartViewChange = (event, newView) => {
    if (newView !== null) {
      setChartView(newView);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!period) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Please select a period to view the monthly statement
        </Typography>
      </Box>
    );
  }

  // Always calculate data even if there are no records


  const grandTotals = calculateGrandTotals();
  const summaryData = prepareSummaryData();
  const chartData   = prepareChartData();
  const pieData     = preparePieData();
  const malariaData = prepareMalariaData();
  const COLORS = ['#10b981', '#ef4444', '#3b82f6', '#f59e0b'];

  console.log("chartData:", chartData); // 👈 ADD HERE

  // Check if we have any actual patient data
  

  const hasChartData = chartData.some(row =>
  row["Insured Admission"] ||
  row["Insured Death"] ||
  row["Non-Insured Admission"] ||
  row["Non-Insured Death"]
);

  return (
    <Box sx={{ width: '100%', p: 2 }}>
     
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryData.map((item, index) => (
          <Grid item xs={12} sm={6} md={3} lg={12/7} key={index}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}dd 100%)`,
              color: 'white',
              height: '100%'
            }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                      {item.value}
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ opacity: 0.7 }}>
                    {item.icon}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Data Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 2, mb: 3 }}>
        <Table size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#4a5568' }}>
              <TableCell 
                rowSpan={3} 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold', 
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  textAlign: 'center',
                  verticalAlign: 'middle'
                }}
              >
                AGE GROUPS
              </TableCell>
              <TableCell 
                colSpan={4} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255,255,255,0.3)'
                }}
              >
                INSURED PATIENTS
              </TableCell>
              <TableCell 
                colSpan={4} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold'
                }}
              >
                NON-INSURED PATIENTS
              </TableCell>
            </TableRow>

            <TableRow sx={{ bgcolor: '#4a5568' }}>
              <TableCell 
                colSpan={2} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  fontSize: '0.85rem'
                }}
              >
                ADMISSION
              </TableCell>
              <TableCell 
                colSpan={2} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  fontSize: '0.85rem'
                }}
              >
                DEATH
              </TableCell>
              <TableCell 
                colSpan={2} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  fontSize: '0.85rem'
                }}
              >
                ADMISSION
              </TableCell>
              <TableCell 
                colSpan={2} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  fontSize: '0.85rem'
                }}
              >
                DEATH
              </TableCell>
            </TableRow>

            <TableRow sx={{ bgcolor: '#4a5568' }}>
              {[...Array(8)].map((_, index) => (
                <TableCell 
                  key={index}
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontSize: '0.75rem',
                    borderRight: index === 1 || index === 3 || index === 5 || index === 7 
                      ? '1px solid rgba(255,255,255,0.3)' 
                      : 'none'
                  }}
                >
                  {index % 2 === 0 ? 'MALE' : 'FEMALE'}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row, index) => (
              <TableRow 
                key={index}
                sx={{ 
                  '&:nth-of-type(odd)': { bgcolor: '#f7fafc' },
                  '&:hover': { bgcolor: '#edf2f7' }
                }}
              >
                <TableCell sx={{ fontWeight: 500, borderRight: '1px solid #e2e8f0' }}>
                  {row.ageGroup}
                </TableCell>
                
                <TableCell align="center">{row.insured.admission.male || 0}</TableCell>
                <TableCell align="center" sx={{ borderRight: '1px solid #e2e8f0' }}>
                  {row.insured.admission.female || 0}
                </TableCell>
                
                <TableCell align="center">{row.insured.death.male || 0}</TableCell>
                <TableCell align="center" sx={{ borderRight: '1px solid #e2e8f0' }}>
                  {row.insured.death.female || 0}
                </TableCell>
                
                <TableCell align="center">{row.nonInsured.admission.male || 0}</TableCell>
                <TableCell align="center" sx={{ borderRight: '1px solid #e2e8f0' }}>
                  {row.nonInsured.admission.female || 0}
                </TableCell>
                
                <TableCell align="center">{row.nonInsured.death.male || 0}</TableCell>
                <TableCell align="center">
                  {row.nonInsured.death.female || 0}
                </TableCell>
              </TableRow>
            ))}

            <TableRow sx={{ bgcolor: '#e2e8f0', fontWeight: 'bold' }}>
              <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #cbd5e0' }}>
                Total All Ages
              </TableCell>
              
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.insured.admission.male}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #cbd5e0' }}>
                {grandTotals.insured.admission.female}
              </TableCell>
              
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.insured.death.male}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #cbd5e0' }}>
                {grandTotals.insured.death.female}
              </TableCell>
              
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.nonInsured.admission.male}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #cbd5e0' }}>
                {grandTotals.nonInsured.admission.female}
              </TableCell>
              
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.nonInsured.death.male}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.nonInsured.death.female}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Malaria Summary Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#4a5568' }}>
              <TableCell 
                colSpan={3}
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}
              >
                SUMMARY OF INPATIENT MALARIA CASES
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#4a5568' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '60%' }}></TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>MALE</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>FEMALE</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: '#f7fafc' } }}>
              <TableCell>Number of Patients below 5 years of Age Admitted with Malaria</TableCell>
              <TableCell align="center">{Math.floor(malariaData.admissionsUnder5 * 0.5)}</TableCell>
              <TableCell align="center">{Math.ceil(malariaData.admissionsUnder5 * 0.5)}</TableCell>
            </TableRow>
            <TableRow sx={{ '&:nth-of-type(even)': { bgcolor: 'white' } }}>
              <TableCell>Number of Patients 5 years and above Admitted with Malaria</TableCell>
              <TableCell align="center">{Math.floor(malariaData.admissionsOver5 * 0.5)}</TableCell>
              <TableCell align="center">{Math.ceil(malariaData.admissionsOver5 * 0.5)}</TableCell>
            </TableRow>
            <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: '#f7fafc' } }}>
              <TableCell>Number of Patients below 5 years of Age Dying of Malaria</TableCell>
              <TableCell align="center">{Math.floor(malariaData.deathsUnder5 * 0.5)}</TableCell>
              <TableCell align="center">{Math.ceil(malariaData.deathsUnder5 * 0.5)}</TableCell>
            </TableRow>
            <TableRow sx={{ '&:nth-of-type(even)': { bgcolor: 'white' } }}>
              <TableCell>Number of Patients 5 years and above Dying of Malaria</TableCell>
              <TableCell align="center">{Math.floor(malariaData.deathsOver5 * 0.5)}</TableCell>
              <TableCell align="center">{Math.ceil(malariaData.deathsOver5 * 0.5)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MonthlyStatementOfInpatients;